import type {
  AccountPreapproval,
  AccountProgramCapacity,
} from "@/lib/brokerAccounts";
import {
  bondRatePercent,
  type BondPreapproval,
  isAccountingLinkConnected,
  isBankLinkConnected,
  type BondSubmission,
} from "@/lib/brokerBonds";
import type { CapacityProject } from "@/lib/capacityRequest";
import {
  phaseLabel,
  readBondLifecycle,
  submissionStatusLabel,
  type BondPhaseKey,
} from "@/lib/bondStateModel";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { formatPremium } from "@/lib/premium";
import { readAccountRequirements } from "@/lib/accountRequirements";
import type { HeaderPill } from "@/lib/submissionShell";
import {
  currentBondRows,
  currentContractPrice,
  programLimits,
} from "@/lib/submissionProfile";

// BRK-S416 — the Submission's Surety Program tab, beneath the program block:
// "This submission against the program", the submissions counted against it
// and the Signal Advance pitch's connection line.
//
// IT SHARES BRK-S415'S READS, AND DECIDES NOTHING (CLAUDE.md #7). The contract
// price, the share of the single limit, the aggregate's "other bonds" and the
// verdict are `programLimits`'s — the Profile tab's Program limits block — so
// the two tabs can never disagree. The verdict is PMS's `capacityCheck`; the
// sums here are display, and none of them is compared with a limit.
//
// DISCRETION: no reason, score or tier, and the vendors behind the two
// connections are never named. Decision 8: "review", never underwriting.

const money = formatPreapprovalMoney;

/** Completion releases the job's share (proposal §10.3). */
const RELEASED: readonly BondPhaseKey[] = ["maintenance", "closed"];

type FitOrder = Pick<
  BondSubmission,
  "id" | "program" | "capacityCheck" | "estimatedPremium" | "closeReason"
> & { bonds?: unknown; lifecycle?: unknown };

/**
 * Which program block the tab shows (BRK-S457): the order's own
 * `preapproval` or the account read's.
 *
 * The account's program wins when it is approved — the header's Program
 * Enrollment button and the Required steps program row read it, and the tab
 * must agree with them. The order's block is shown only when it is the more
 * advanced fact (an approval on the order itself) or the account holds no
 * program; a `pending` or not-granted order block never hides an approved
 * account. While the account read is out or failed, an order approval is
 * still true and anything less waits for the read (no "Not enrolled" guess).
 * Pure precedence between two served blocks — no status is derived (#7).
 */
export function shownProgram(
  order: BondPreapproval | null | undefined,
  account: AccountPreapproval | null | "loading",
): AccountPreapproval | null | "loading" {
  const ownApproval =
    order?.status === "approved" && order.source !== "account";
  const accountApproved =
    account !== null && account !== "loading" && account.status === "approved";
  if (accountApproved && !ownApproval) return account;
  if (order?.status === "approved") return foldOrderBlock(order);
  if (account === null || account === "loading") return account;
  return order ? foldOrderBlock(order) : account;
}

/** The order's block in the account's vocabulary: `pending` reads `none` (the
 *  account's block cannot be pending). The rest is carried straight across —
 *  the two blocks publish the same closed vocabulary, and a fold would be this
 *  page deciding which program an insured holds (BRK-S161). */
function foldOrderBlock(order: BondPreapproval): AccountPreapproval {
  return {
    status: order.status === "pending" ? "none" : order.status,
    programType: order.programType,
    expiresAt: order.expiresAt,
    terms: order.terms,
    evaluatedAt: order.evaluatedAt,
    indicationAvailable: order.indicationAvailable,
  };
}

export interface ProgramFitView {
  /** PMS's verdict; null when it published no `capacityCheck`. */
  pill: HeaderPill | null;
  /** The current bonds' contract price; null when none is published. */
  contract: string | null;
  /** "115% of $1,000,000" — display only; null without a price or limit. */
  singleShare: string | null;
  /** The label always; the figure only when PMS published one (never $0). */
  premium: { label: string; value: string | null };
  /** Null when the aggregate cannot be read (and the job still counts). */
  aggregate: { label: string; value: string } | null;
}

function phaseOf(order: { lifecycle?: unknown }): BondPhaseKey | null {
  return readBondLifecycle(order.lifecycle)?.phase ?? null;
}

/**
 * The premium line. PMS's `estimatedPremium` only: "Estimated premium at
 * 2.50%" while the bid is out or the price is the estimate, "Premium to be
 * invoiced" once a won bid confirmed the final price. An absent, zero or
 * malformed amount shows no figure — a $0 premium is a claim (a free bond).
 */
function premiumLine(
  order: FitOrder,
  phase: BondPhaseKey | null,
): ProgramFitView["premium"] {
  const p = order.estimatedPremium;
  const amount =
    p &&
    typeof p.amount === "number" &&
    Number.isFinite(p.amount) &&
    p.amount > 0
      ? p.amount
      : null;
  if (
    p?.basis === "final_contract_price" &&
    phase !== "bid" &&
    amount !== null
  ) {
    return { label: "Premium to be invoiced", value: formatPremium(amount) };
  }
  const rate = order.program?.rate;
  const label =
    typeof rate === "number" && rate > 0
      ? `Estimated premium at ${bondRatePercent(rate)}%`
      : "Estimated premium";
  return { label, value: amount === null ? null : formatPremium(amount) };
}

/**
 * "This submission against the program", or null when there is no program to
 * measure against (the tab then says so) or the order view predates
 * `program` (PMS-S720).
 */
export function programFit({
  order,
  capacity,
  projects,
}: {
  order: FitOrder;
  capacity: AccountProgramCapacity | null;
  projects: readonly CapacityProject[] | null;
}): ProgramFitView | null {
  if (!order.program) return null;
  const limits = programLimits({ order, capacity, projects });
  if (limits === null || limits.noProgram) return null;
  const phase = phaseOf(order);
  const contract = currentContractPrice(currentBondRows(order));

  // The Profile's own pill (decision 8 drops the wireframe's "· underwriter
  // review").
  const pill = limits.pill;

  let aggregate: ProgramFitView["aggregate"] = null;
  if (phase !== null && RELEASED.includes(phase)) {
    const why =
      phase === "maintenance"
        ? "released in Maintenance"
        : order.closeReason === "not_awarded"
          ? "not awarded"
          : "released";
    aggregate = { label: "Aggregate impact", value: `Not counted · ${why}` };
  } else if (limits.aggregate) {
    const { other, thisBond, limit } = limits.aggregate;
    aggregate = {
      label:
        phase === "performance_period"
          ? "Aggregate with this bond"
          : "Aggregate once issued",
      value: `${money(other + (thisBond ?? 0))} of ${money(limit)}`,
    };
  }

  return {
    pill,
    contract: contract === null ? null : money(contract),
    singleShare: limits.single
      ? `${String(limits.single.percent)}% of ${money(limits.single.limit)}`
      : null,
    premium: premiumLine(order, phase),
    aggregate,
  };
}

export interface CountedRow {
  orderId: string;
  number: string;
  projectName: string | null;
  href: string;
  /** Where the job is: "Bid bond" for an open hold, "Performance Bond
   *  Period" for a locked one, this order's own phase (and its review). */
  status: string;
  amount: string | null;
  counts: string;
  /** This submission — highlighted. */
  current: boolean;
}

/**
 * The submissions counted against the program: PMS's capacity-projects rows
 * (released orders are already left out upstream), with this one marked. When
 * this order holds nothing it is added last — "Not counted" once released,
 * "Once issued" before — so the broker sees where it stands. Empty while the
 * read is unavailable.
 */
export function countedSubmissions(
  order: Pick<BondSubmission, "id" | "number"> & {
    bonds?: unknown;
    lifecycle?: unknown;
  },
  projects: readonly CapacityProject[] | null,
): CountedRow[] {
  if (projects === null) return [];
  const rows: CountedRow[] = projects.map((p) => ({
    orderId: p.orderId,
    number: p.orderNumber,
    projectName: p.projectName,
    href: `/surety/submissions/${encodeURIComponent(p.orderId)}`,
    // An open hold is an issued bid's estimate or a won bid's price; a
    // locked one an issued P&P's final price (PMS-S493's two states).
    status: p.state === "locked" ? "Performance Bond Period" : "Bid bond",
    amount: money(p.amount),
    counts: "Aggregate",
    current: p.orderId === order.id,
  }));
  const current = rows.find((r) => r.current);
  if (current) current.status = ownStatus(order);
  if (!rows.some((r) => r.current)) {
    const phase = phaseOf(order);
    const released = phase !== null && RELEASED.includes(phase);
    const contract = currentContractPrice(currentBondRows(order));
    rows.push({
      orderId: order.id,
      number: order.number,
      projectName: null,
      href: `/surety/submissions/${encodeURIComponent(order.id)}`,
      status: released ? "Released" : ownStatus(order),
      amount: contract === null ? null : money(contract),
      counts: released ? "Not counted" : "Projected",
      current: true,
    });
  }
  return rows;
}

/** This order's own row status: its phase, and in the bid phase its review
 *  ("Bid bond · In review", the wireframe's "Bid bond · In underwriting"). */
function ownStatus(
  order: { lifecycle?: unknown } & Partial<Pick<BondSubmission, "status">>,
): string {
  const phase = phaseOf(order);
  if (phase === null) return "In progress";
  const label = phaseLabel(phase);
  if (phase === "bid" && order.status === "underwriter_review") {
    return `${label} · ${submissionStatusLabel(order.status)}`;
  }
  return label;
}

export interface ProgramUtilizationView {
  /** What the account holds now. */
  used: number;
  /** This bond's contract price, striped, while it is not yet held; null
   *  once held or released. */
  projected: number | null;
  limit: number;
  /** The limit less what is held now; never negative. */
  available: number;
}

/**
 * The Surety Program box's "Aggregate utilized" bar (the October
 * wireframe): the account's served `aggregateUtilized` against the program's
 * aggregate limit, and — striped — this bond's contract price while the job
 * holds nothing yet. Display sums of served figures; no verdict.
 */
export function programUtilization({
  order,
  capacity,
  projects,
}: {
  order: FitOrder;
  capacity: AccountProgramCapacity | null;
  projects: readonly CapacityProject[] | null;
}): ProgramUtilizationView | null {
  const limit = order.program?.aggregateLimit ?? null;
  if (capacity === null || limit === null || limit <= 0) return null;
  const phase = phaseOf(order);
  const released = phase !== null && RELEASED.includes(phase);
  const held = projects?.some((p) => p.orderId === order.id) ?? true;
  const contract = currentContractPrice(currentBondRows(order));
  const used = capacity.aggregateUtilized;
  return {
    used,
    projected: !released && !held && contract !== null ? contract : null,
    limit,
    available: Math.max(0, limit - used),
  };
}

/**
 * The Signal Advance pitch's connection line, from what the order publishes:
 * the Express program is credit-based, so its credit check is done; each
 * link's own state; and the Signal Advance step's status — `in_review` is
 * everything in, with Signal (met by a link or by documents, so the line does
 * not say which).
 */
export function advanceConnectionLine(
  order: Pick<BondSubmission, "requirements" | "bankLink" | "accountingLink">,
): string[] {
  const advance = readAccountRequirements(order.requirements)?.find(
    (r) => r.kind === "signal_advance",
  );
  if (advance?.status === "in_review") {
    return [
      "Credit check done",
      "Bank and accounting received",
      "Signal review in progress",
    ];
  }
  const bank =
    order.bankLink?.state !== undefined &&
    isBankLinkConnected(order.bankLink.state);
  const accounting =
    order.accountingLink !== undefined &&
    isAccountingLinkConnected(order.accountingLink.state);
  return [
    "Credit check done",
    bank ? "Bank connected" : "Bank not connected",
    accounting ? "Accounting connected" : "Accounting not connected",
    "Signal reviews once both connect",
  ];
}
